import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash2 } from "lucide-react";
import { MeasurementTestLink } from "./measurement-test-link";
import type { FoodMeasurementTypeId, TestTemplateAttributeId } from "@brewbase/db/types";

export function TypeList<T extends { id: number; name: string }>({
  items,
  secondaryKey,
  secondaryPlaceholder,
  namePlaceholder,
  onAdd,
  onDelete,
  isAdding,
  isDeleting,
}: {
  items: T[];
  secondaryKey?: keyof T;
  secondaryPlaceholder?: string;
  namePlaceholder: string;
  onAdd: (name: string, secondary: string) => void;
  onDelete: (id: T["id"]) => void;
  isAdding: boolean;
  isDeleting: boolean;
}) {
  const [name, setName] = useState("");
  const [secondary, setSecondary] = useState("");
  const nameRef = useRef<HTMLInputElement>(null);

  const handleAdd = () => {
    if (!name.trim()) return;
    onAdd(name.trim(), secondary.trim());
    setName("");
    setSecondary("");
    nameRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <div className="space-y-1">
      {items.length > 0 && (
        <div className="rounded-md border divide-y">
          {items.map((item) => (
            <div
              key={item.id}
              className="flex items-center gap-3 px-3 py-2 group hover:bg-muted/40"
            >
              <span className="flex-1 text-sm">{item.name}</span>
              {secondaryKey && (
                <span className="text-xs text-muted-foreground w-20 text-right">
                  {String(item[secondaryKey] ?? "")}
                </span>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                disabled={isDeleting}
                onClick={() => onDelete(item.id)}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {items.length === 0 && (
        <div className="rounded-md border border-dashed p-4 text-center text-xs text-muted-foreground">
          No entries yet. Add one below.
        </div>
      )}

      <div className="flex gap-2 pt-1">
        <Input
          ref={nameRef}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={namePlaceholder}
          className="h-8 flex-1"
        />
        {secondaryKey && (
          <Input
            value={secondary}
            onChange={(e) => setSecondary(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={secondaryPlaceholder}
            className="h-8 w-28"
          />
        )}
        <Button
          size="sm"
          variant="outline"
          className="h-8"
          disabled={!name.trim() || isAdding}
          onClick={handleAdd}
        >
          <Plus className="h-3.5 w-3.5 mr-1" />
          Add
        </Button>
      </div>
    </div>
  );
}

export function MeasurementTypeList<
  T extends {
    id: FoodMeasurementTypeId;
    name: string;
    default_unit: string | null;
    test_template_attribute_id?: TestTemplateAttributeId | null;
    linked_template_id?: number | null;
    linked_template_name?: string | null;
    linked_attribute_name?: string | null;
  },
>({
  items,
  onDelete,
  isDeleting,
}: {
  items: T[];
  onDelete: (id: T["id"]) => void;
  isDeleting: boolean;
}) {
  if (items.length === 0) {
    return (
      <div className="rounded-md border border-dashed p-8 text-center text-xs text-muted-foreground">
        No measurement types yet. Add one to get started.
      </div>
    );
  }

  return (
    <div className="rounded-md border overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-4 px-3 py-2 bg-muted/40 border-b">
        <span className="flex-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Name
        </span>
        <span className="flex-[0.5] text-xs font-medium uppercase tracking-wide text-muted-foreground shrink-0">
          Unit
        </span>
        <span className="flex-2 text-xs font-medium uppercase tracking-wide text-muted-foreground shrink-0">
          Test attribute
        </span>
        <div className="w-6 shrink-0" />
      </div>

      {/* Rows */}
      <div className="divide-y">
        {items.map((item) => (
          <div
            key={item.id}
            className="flex items-center gap-4 px-3 py-2.5 group hover:bg-muted/40 min-w-0"
          >
            <span className="flex-1 text-sm truncate">{item.name}</span>
            <span className="flex-[0.5] text-xs text-muted-foreground font-mono shrink-0">
              {item.default_unit ?? "—"}
            </span>
            <div className="flex-2 shrink-0 min-w-0">
              <MeasurementTestLink
                measurementTypeId={item.id}
                linkedAttributeId={item.test_template_attribute_id ?? null}
                linkedTemplateName={item.linked_template_name ?? null}
                linkedAttributeName={item.linked_attribute_name ?? null}
              />
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
              disabled={isDeleting}
              onClick={() => onDelete(item.id)}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
